import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import type { PayloadAction } from "@reduxjs/toolkit";
import type {
  ISearchResultItemTypeArt,
  ISearchResultItemTypeTag,
  ISearchResultItemTypeCollection,
  ISearchHistoryItem,
  ILegacySearchRequest,
  ILegacySearchResponse,
  IGetSearchHistoryCat2Request,
  IGetSearchHistoryCat2Response,
  IClearSearchHistoryCSRFRequest,
  IClearSearchHistoryCSRFResponse,
  ISaveSearchQueryCat2Request,
  ISaveSearchQueryCat2Response,
  IApiError,
  IReduxRequestNode,
  IResponseErrorDetails,
  ISearchResponse,
  TSearchAllItem,
} from "@litres-libs/types";
import { Request } from "../../request/Request";
import { addHydrateReducer } from "../../utils/hydrateReducer";
import { searchApi } from "../../rtkq/search/search.api";

export interface ISearchState {
  query: string | undefined;
  appliedCorrection?: {
    from_query: string;
    to_query: string;
  };
  searchResults?: (
    | TSearchAllItem
    | ISearchResultItemTypeArt
    | ISearchResultItemTypeTag
    | ISearchResultItemTypeCollection
  )[];
  history?: ISearchHistoryItem[];
  loading: boolean;
  error?: IResponseErrorDetails | Record<string, unknown> | IApiError;
  isInitiated: boolean;
}

const initialState: ISearchState = {
  query: "",
  searchResults: [],
  history: [],
  loading: false,
  error: {},
  isInitiated: false,
};

export type APIRequestParams<T = object> = T & {
  onSuccess?: (success: ISearchResponse) => void;
  onError?: (success: IApiError) => void;
};

/**
 * Запрос поиска по текстовому ключу
 */
export const getSearchData = createAsyncThunk(
  "global/search/getSearchData",
  async (params: APIRequestParams<{ query: string }>) => {
    const {
      query: q,
      onSuccess = (): void => {},
      onError = (): void => {},
    } = params;

    const response = new Request.GUJ<
      ILegacySearchRequest,
      ILegacySearchResponse
    >({
      url: "/pages/search_slice/",
      provider: {
        id: "SEARCH",
      },
      request: {
        method: "GET",
        params: {
          json: 1,
          limit: 9,
          gu_ajax: true,
          q,
        },
      },
      onSuccess,
      onError,
    });

    return await response.fetch();
  }
);

/**
 * Получение истории поиска пользователя
 */
export const getSearchHistory = createAsyncThunk(
  "global/search/getSearchHistory",
  async (params: APIRequestParams<{ limit: [number, number] }>) => {
    const {
      limit,
      onSuccess = (): void => {},
      onError = (): void => {},
    } = params;

    const response = new Request.Cat2<
      IGetSearchHistoryCat2Request,
      IGetSearchHistoryCat2Response
    >({
      url: "/",
      provider: {
        id: "r_last_search",
      },
      request: {
        method: "POST",
        params: {
          limit,
        },
      },
      onSuccess,
      onError,
    });

    return await response.fetch();
  }
);

/**
 * Запрос на полную очистку истории поиска пользователя
 */
export const clearSearchHistory = createAsyncThunk(
  "global/search/clearSearchHistory",
  async (
    params: APIRequestParams = {
      onSuccess: (): void => {},
      onError: (): void => {},
    }
  ) => {
    const { onSuccess, onError } = params;

    const response = new Request.CSRF<
      IClearSearchHistoryCSRFRequest,
      IClearSearchHistoryCSRFResponse
    >({
      url: "/",
      provider: {
        id: "csrf_clear_search_history",
      },
      request: {
        method: "POST",
        data: {
          action: "clean_search_history",
          gu_ajax: true,
        },
      },
      onSuccess,
      onError,
    });

    return await response.fetch();
  }
);

/**
 * Запрос для сохранения нового итема поиска на бэке
 */
export const saveSearchQuery = createAsyncThunk(
  "global/search/saveSearchQuery",
  async (params: APIRequestParams<{ query: string }>) => {
    const {
      query: q,
      onSuccess = (): void => {},
      onError = (): void => {},
    } = params;

    const response = new Request.Cat2<
      ISaveSearchQueryCat2Request,
      ISaveSearchQueryCat2Response
    >({
      url: "/",
      provider: {
        id: "w_last_search",
      },
      request: {
        method: "POST",
        params: {
          q,
        },
      },
      onSuccess,
      onError,
    });

    return await response.fetch();
  }
);

export const searchSlice = createSlice({
  name: "global/search",
  initialState,
  reducers: {
    setSearchData: (
      state: ISearchState,
      action: PayloadAction<ISearchState["searchResults"]>
    ) => {
      state.searchResults = action.payload;
    },
    setSearchHistory: (
      state: ISearchState,
      action: PayloadAction<ISearchState["history"]>
    ) => {
      state.history = action.payload;
    },
    refreshSearchResults: (state: ISearchState) => {
      state.searchResults = [];
    },
  },
  extraReducers: builder => {
    addHydrateReducer(builder, "global.search");
    /**
     * Запрос поиска по текстовому ключу
     */
    builder.addCase(getSearchData.pending, (state: ISearchState) => {
      state.loading = true;
    });
    builder.addCase(
      getSearchData.fulfilled,
      (
        state: ISearchState,
        action: PayloadAction<
          IReduxRequestNode<ILegacySearchRequest, ILegacySearchResponse>
        >
      ) => {
        state.loading = false;
        state.searchResults = action.payload.response?.result;
        state.query = action.payload.response?.search_string;

        // TODO: Ошибку точно не надо как-то визуально обработать в интерфейсе?
        if (action.payload.error) {
          console.error("Ошибка запроса getSearchData", action.payload.error);
        }
      }
    );

    /**
     * Запрос на получение истории поиска
     */
    builder.addCase(getSearchHistory.pending, (state: ISearchState) => {
      state.loading = true;
    });
    builder.addCase(
      getSearchHistory.fulfilled,
      (
        state: ISearchState,
        action: PayloadAction<
          IReduxRequestNode<
            IGetSearchHistoryCat2Request,
            IGetSearchHistoryCat2Response
          >
        >
      ) => {
        state.loading = false;
        state.history = action.payload.response?.requests;

        // TODO: Ошибку точно не надо как-то визуально обработать в интерфейсе?
        if (action.payload.error) {
          console.error(
            "Ошибка запроса getSearchHistory",
            action.payload.error
          );
        }
      }
    );

    /**
     * Запрос на очистку истории поиска
     */
    builder.addCase(clearSearchHistory.pending, (state: ISearchState) => {
      state.loading = true;
    });
    builder.addCase(
      clearSearchHistory.fulfilled,
      (
        state: ISearchState,
        action: PayloadAction<
          IReduxRequestNode<
            IClearSearchHistoryCSRFRequest,
            IClearSearchHistoryCSRFResponse
          >
        >
      ) => {
        state.loading = false;
        state.history = [];

        // TODO: Ошибку точно не надо как-то визуально обработать в интерфейсе?
        if (action.payload.error) {
          console.error(
            "Ошибка запроса clearSearchHistory",
            action.payload.error
          );
        }
      }
    );

    /**
     * Запрос на получение истории поиска
     */
    builder.addCase(saveSearchQuery.pending, (state: ISearchState) => {
      state.loading = true;
    });
    builder.addCase(
      saveSearchQuery.fulfilled,
      (
        state: ISearchState,
        action: PayloadAction<
          IReduxRequestNode<
            ISaveSearchQueryCat2Request,
            ISaveSearchQueryCat2Response
          >
        >
      ) => {
        state.loading = false;

        // TODO: Ошибку точно не надо как-то визуально обработать в интерфейсе?
        if (action.payload.error) {
          console.error("Ошибка запроса saveSearchQuery", action.payload.error);
        }
      }
    );

    builder.addMatcher(
      searchApi.endpoints.getSearch.matchPending,
      (state: ISearchState, action) => {
        state.loading = true;
        state.query = action.meta.arg.originalArgs.q;
        state.error = {};
      }
    );
    builder.addMatcher(
      searchApi.endpoints.getSearch.matchFulfilled,
      (state: ISearchState, { payload }) => {
        if (payload) {
          const { data } = payload;
          state.searchResults = data as TSearchAllItem[];
          state.isInitiated = true;
          state.loading = false;
        }
      }
    );
    builder.addMatcher(
      searchApi.endpoints.getSearch.matchRejected,
      (state: ISearchState) => {
        state.loading = false;
        state.isInitiated = true;
      }
    );
  },
});

export const { setSearchData, setSearchHistory, refreshSearchResults } =
  searchSlice.actions;

export default searchSlice.reducer;
